import { VIZ_CATEGORICAL } from '@splunk/visualization-color-palettes';
import {
    COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
    enhanceOptionsSchemaWithDynamicOptions,
    getPattern,
    LAYER_TYPE,
} from '../utils';

export const mapOptionsSchema = enhanceOptionsSchemaWithDynamicOptions({
    backgroundColor: {
        default: '> themes.defaultBackgroundColor',
        description:
            'Specify a color for the background. You might use a dataSource to apply the color. The default for enterprise light is "#FFFFFF". The default for enterprise dark is "#000000". The default for prisma dark is "#0B0C0E".',
        pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
        type: 'string',
    },
    baseLayerTileServer: {
        type: 'string',
        description: 'Specify the URL for the set of tiles that make up the base map.',
    },
    baseLayerTileServerType: {
        type: 'string',
        description: 'Specify the type of tile set for the base map.',
        pattern: getPattern(['vector', 'raster']),
    },
    center: {
        type: 'array',
        description:
            'Specify the center of the map in coordinates, in the form of [latitude, longitude]. If you don’t provide coordinates, the map automatically centers, based on available location data. If there is no available location data, the center of the map defaults to [37.7749, -122.4194].',
        items: { type: 'number' },
    },
    layers: {
        // The option merging of defaults and data binding with editing UI happens with the default defined below.
        // The inner defaults for properties within anyOf objects is used only for code intellisense in UDF to populate options.
        default: [
            {
                type: LAYER_TYPE.Bubble,
                latitude: '> primary | seriesByName("latitude")',
                longitude: '> primary | seriesByName("longitude")',
                seriesColors: VIZ_CATEGORICAL,
                dataValues: '> primary | seriesByType("number")',
                dataColors: '', // default to an empty string to prioritize seriesColors
                bubbleSize:
                    '> primary | frameWithoutSeriesNames("geobin", "latitude", "longitude") | frameBySeriesTypes("number")',
                additionalTooltipFields: [],
                resultLimit: 1000,
            },
            {
                type: LAYER_TYPE.Marker,
                latitude: '> primary | seriesByName("latitude")',
                longitude: '> primary | seriesByName("longitude")',
                seriesColors: VIZ_CATEGORICAL,
                dataValues: '> primary | seriesByType("number")',
                dataColors: '', // default to an empty string to prioritize seriesColors
                additionalTooltipFields: [],
                resultLimit: 1000,
            },
            {
                type: LAYER_TYPE.Choropleth,
                areaIds: '> primary | seriesByType("string")',
                areaValues: '> primary | seriesByType("number")',
                dataColors: '> areaValues | gradient()',
                additionalTooltipFields: [],
                resultLimit: 1000,
                choroplethEmptyAreaColor: '> themes.defaultChoroplethEmptyAreaColor',
                choroplethStrokeColor: 'transparent',
                choroplethOpacity: 0.8,
            },
        ],
        type: 'array',
        description:
            'Specify the optional data visualization layers over the base map. If empty, only the base map appears.',
        items: {
            anyOf: [
                {
                    type: 'object',
                    properties: {
                        type: {
                            description:
                                'Specify the type of data layer method to display points on the map. For example, “bubble”, “marker”, or "choropleth".',
                            type: 'string',
                            pattern: getPattern([
                                LAYER_TYPE.Marker,
                                LAYER_TYPE.Bubble,
                                LAYER_TYPE.Choropleth,
                            ]),
                            default: LAYER_TYPE.Marker,
                        },
                        latitude: {
                            description:
                                'Specify a dataSource series to apply latitude coordinates for data points displayed on the map. This applies to layers where type: "bubble" or type: "marker".',
                            type: ['string', 'array'],
                            items: { type: 'number' },
                            default: '> primary | seriesByName("latitude")',
                        },
                        longitude: {
                            description:
                                'Specify a dataSource series to apply longitude coordinates for data points displayed on the map. This applies to layers where type: "bubble" or type: "marker".',
                            type: ['string', 'array'],
                            items: { type: 'number' },
                            default: '> primary | seriesByName("longitude")',
                        },
                        seriesColors: {
                            description:
                                'Specify the colors to use for data points. If the dataColors option is specified, the seriesColors option is ignored. The seriesColors option applies to layers where type: "bubble" or type: "marker".',
                            type: 'array',
                            items: {
                                type: 'string',
                                pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
                            },
                            default: VIZ_CATEGORICAL,
                        },
                        dataColors: {
                            description:
                                'Specify the colors to use for data points. You can use an array of colors to be picked from, or you can use a dataSource to apply dynamic coloring. For example, “> primary | seriesByName("status") | matchValue(colorMatchConfig)”. When type: "choropleth", dataColors is defined by default, and the opacities are shown at 80% of maximum to allow textual information to be visible in the map. By default, the dataColors option is empty for "bubble" or "marker" layers to prioritize seriesColors. For "bubble" layers, dataColors requires bubbleSize to have a single series configured. If multiple series are set for bubbleSize, only the first pie slice reflects the colors.',
                            type: ['string', 'array'],
                            items: { type: 'string' },
                            default: '',
                        },
                        dataValues: {
                            description:
                                'Specify the data series used to encode colors for dynamic coloring in layers of type "marker" or "bubble".',
                            type: ['string', 'array'],
                            items: { type: 'number' },
                            default: '> primary | seriesByType("number")',
                        },
                        additionalTooltipFields: {
                            description:
                                'Specify the fields to add to the default set of tooltips. Tooltips appear when you hover over data points. These fields and their corresponding values are shown in addition to the ones displayed by default.',
                            type: 'array',
                            items: { type: 'string' },
                            default: [],
                        },
                        tooltipHeaderField: {
                            description:
                                'Specify the field to add to the tooltip header. Tooltips appear when you hover over data points. Only the value of this field will be displayed as the header.',
                            type: 'string',
                        },
                        resultLimit: {
                            description:
                                'Specify the maximum number of data points to render on the map. Adjust the value to improve performance.',
                            type: 'number',
                            default: 1000,
                        },
                    },
                },
                {
                    type: 'object',
                    properties: {
                        type: {
                            description:
                                'Specify the type of data layer method to display points on the map. For example, “bubble”, “marker”, or "choropleth".',
                            type: 'string',
                            pattern: getPattern([
                                LAYER_TYPE.Marker,
                                LAYER_TYPE.Bubble,
                                LAYER_TYPE.Choropleth,
                            ]),
                            default: LAYER_TYPE.Choropleth,
                        },
                        areaIds: {
                            description:
                                'Specify a dataSource series to identify each area in the choropleth map layer. This applies to layers where type: "choropleth".',
                            type: 'array',
                            items: { type: 'string' },
                            default: '> primary | seriesByType("string")',
                        },
                        areaValues: {
                            description:
                                'Specify a dataSource series that indicates the values for the choropleth map areas. This applies to layers where type: "choropleth".',
                            type: 'array',
                            items: { type: 'number' },
                            default: '> primary | seriesByType("number")',
                        },
                        dataColors: {
                            description:
                                'Specify the colors to use for data points. You can use an array of colors to be picked from, or you can use a dataSource to apply dynamic coloring. For example, “> primary | seriesByName("status") | matchValue(colorMatchConfig)”. When type: "choropleth", dataColors is defined by default. By default, the dataColors option is not defined for type "bubble" or "marker".',
                            type: ['string', 'array'],
                            items: { type: 'string' },
                            default: '> areaValues | gradient()',
                        },
                        additionalTooltipFields: {
                            description:
                                'Specify the fields to add to the default set of tooltips. Tooltips appear when you hover over data points. These fields and their corresponding values are shown in addition to the ones displayed by default.',
                            type: 'array',
                            items: { type: 'string' },
                            default: [],
                        },
                        tooltipHeaderField: {
                            description:
                                'Specify the field to add to the tooltip header. Tooltips appear when you hover over data points. Only the value of this field will be displayed as the header.',
                            type: 'string',
                        },
                        resultLimit: {
                            description:
                                'Specify the maximum number of data points to render on the map. Adjust the value to improve performance.',
                            type: 'number',
                            default: 1000,
                        },
                        source: {
                            description:
                                'Source for the geodata for choropleth layer: "us" or "world", and will override data specified in the SPL geom command.',
                            pattern: getPattern(['geo://default/us', 'geo://default/world']),
                            type: 'string',
                        },
                        choroplethEmptyAreaColor: {
                            description:
                                'Specify the color used for empty choropleth areas when "type": "choropleth", and will only work when "source" option is correctly configured.',
                            pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
                            type: 'string',
                            default: '> themes.defaultChoroplethEmptyAreaColor',
                        },
                        choroplethStrokeColor: {
                            description:
                                'Specify the stroke color for choropleth area outlines. The hex value format should be "#FFFFFF".',
                            pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
                            type: 'string',
                            default: 'transparent',
                        },
                        choroplethOpacity: {
                            description:
                                'Specify the opacity of choropleth areas. Choose a number in the range of 0 - 1 (inclusive). You can also express the value as a percentage. For example, "0.8" in code or "80%" in UI.',
                            type: 'number',
                            default: 0.8,
                        },
                    },
                },
                {
                    type: 'object',
                    properties: {
                        type: {
                            description:
                                'Specify the type of data layer method to display points on the map. For example, “bubble”, “marker”, or "choropleth".',
                            type: 'string',
                            pattern: getPattern([
                                LAYER_TYPE.Marker,
                                LAYER_TYPE.Bubble,
                                LAYER_TYPE.Choropleth,
                            ]),
                            default: LAYER_TYPE.Bubble,
                        },
                        bubbleSize: {
                            description:
                                'Specify the data column to encode bubble size. This applies to layers where type: "bubble". If a single data column is specified, each bubble appears as a circle. When multiple columns are provided, bubbles are displayed as pie charts, with each slice representing a different column.',
                            type: ['string', 'array'],
                            items: { type: 'number' },
                            default:
                                '> primary | frameWithoutSeriesNames("geobin", "latitude", "longitude") | frameBySeriesTypes("number")',
                        },
                        latitude: {
                            description:
                                'Specify a dataSource series to apply latitude coordinates for data points displayed on the map. This applies to layers where type: "bubble" or type: "marker".',
                            type: ['string', 'array'],
                            items: { type: 'number' },
                            default: '> primary | seriesByName("latitude")',
                        },
                        longitude: {
                            description:
                                'Specify a dataSource series to apply longitude coordinates for data points displayed on the map. This applies to layers where type: "bubble" or type: "marker".',
                            type: ['string', 'array'],
                            items: { type: 'number' },
                            default: '> primary | seriesByName("longitude")',
                        },
                        seriesColors: {
                            description:
                                'Specify the colors to use for data points. If the dataColors option is specified, the seriesColors option is ignored. The seriesColors option applies to layers where type: "bubble" or type: "marker".',
                            type: 'array',
                            items: {
                                type: 'string',
                                pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
                            },
                            default: VIZ_CATEGORICAL,
                        },
                        dataColors: {
                            description:
                                'Specify the colors to use for data points. You can use an array of colors to be picked from, or you can use a dataSource to apply dynamic coloring. For example, “> primary | seriesByName("status") | matchValue(colorMatchConfig)”. When type: "choropleth", dataColors is defined by default, and the opacities are shown at 80% of maximum to allow textual information to be visible in the map. By default, the dataColors option is empty for "bubble" or "marker" layers to prioritize seriesColors. For "bubble" layers, dataColors requires bubbleSize to have a single series configured. If multiple series are set for bubbleSize, only the first pie slice reflects the colors.',
                            type: ['string', 'array'],
                            items: { type: 'string' },
                            default: '',
                        },
                        dataValues: {
                            description:
                                'Specify the data series used to encode colors for dynamic coloring in layers of type "marker" or "bubble".',
                            type: ['string', 'array'],
                            items: { type: 'number' },
                            default: '> primary | seriesByType("number")',
                        },
                        additionalTooltipFields: {
                            description:
                                'Specify the fields to add to the default set of tooltips. Tooltips appear when you hover over data points. These fields and their corresponding values are shown in addition to the ones displayed by default.',
                            type: 'array',
                            items: { type: 'string' },
                            default: [],
                        },
                        tooltipHeaderField: {
                            description:
                                'Specify the field to add to the tooltip header. Tooltips appear when you hover over data points. Only the value of this field will be displayed as the header.',
                            type: 'string',
                        },
                        resultLimit: {
                            description:
                                'Specify the maximum number of data points to render on the map. Adjust the value to improve performance.',
                            type: 'number',
                            default: 1000,
                        },
                    },
                },
            ],
        },
    },
    scaleUnit: {
        default: 'metric',
        type: 'string',
        description: 'Specify the scale unit to use.',
        pattern: getPattern(['metric', 'imperial']),
    },
    showBaseLayer: {
        default: true,
        type: 'boolean',
        description: 'Specify if showing the base map.',
    },
    showScale: {
        default: true,
        type: 'boolean',
        description: 'Specify whether to display the scale on the bottom left.',
    },
    showZoomControls: {
        default: true,
        type: 'boolean',
        description: 'Specify whether to display the control panel of zoom on the top left.',
    },
    zoom: {
        description: 'Specify the zoom level of the map.',
        type: 'number',
    },
    showCoordinates: {
        default: true,
        type: 'boolean',
        description: 'Specify whether to display coordinates (latitude and longitude) in the tooltip.',
    },
    icon: {
        default: '',
        type: 'string',
        description:
            'Specify the url for an svg to display an icon inside bubbles for map. This applies to layers where type: "bubble".',
    },
});
